/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import * as Sentry from '@sentry/react';

import loggerSingleton from 'js/app/loggerSingleton';
import { useLocation, useParams } from 'js/lib/useRouter';

import { Button, Typography, Typography2, useTheme } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { SuccessFilledIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { LevelSetAttempt } from 'bundles/program-skills-objectives/components/ProgramSkillsObjectivesPage/LevelSetGraphqlQueries';
import { levelSetBackgroundURL } from 'bundles/skills-common/private/levelset-card/LevelSetCard';

import _t from 'i18n!nls/program-skills-objectives';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<'a'>>(
  // TODO(ppaskaris): Ask how to type this.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Button
);

export const variants = {
  SMALL: 'small',
  LARGE: 'large',
} as const;
type VariantValues = typeof variants[keyof typeof variants];

type PropsFromCaller = {
  skillId: string;
  variant: VariantValues;
  levelSetAttempt: LevelSetAttempt;
};

type Props = PropsFromCaller;

type VariantStyleMap = { [variant in VariantValues]: Record<string, any> };

const variantStyleMap: VariantStyleMap = {
  [variants.LARGE]: {
    container: (theme: Theme) =>
      css({
        padding: theme.spacing(24),
        background: `${theme.palette.gray[100]} url(${levelSetBackgroundURL})
        top right no-repeat`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
        // Aligns the box with the right of the skill score since it is currently larger
        float: 'right',
      }),
    supportText: (theme: Theme) => css({ marginRight: theme.spacing(12) }),
    ctaButton: (theme: Theme) =>
      css({
        width: 'fit-content',
        marginTop: theme.spacing(12),
      }),
  },
  [variants.SMALL]: {
    container: (theme: Theme) =>
      css({
        padding: theme.spacing(12, 16),
        background: `${theme.palette.gray[100]} url(${levelSetBackgroundURL})
        top right no-repeat`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minWidth: 350,
        width: '100%',
        marginTop: theme.spacing(8),
        // Aligns the box with the right of the skill score since it is currently larger
        float: 'right',
        [theme.breakpoints.down('xs')]: {
          minWidth: 0,
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          marginBottom: theme.spacing(12),
        },
      }),
    supportText: (theme: Theme) =>
      css({
        marginRight: theme.spacing(12),
        [theme.breakpoints.down('xs')]: {
          marginRight: 0,
          marginBottom: theme.spacing(12),
        },
      }),
    // Function for parity with large variant
    ctaButton: () =>
      css({
        display: 'block',
        '.cds-button-label': {
          // Stops text wrapping
          whiteSpace: 'nowrap',
        },
      }),
    completedContainer: (theme: Theme) =>
      css({
        display: 'flex',
        padding: theme.spacing(8, 0),
        columnGap: theme.spacing(4),
      }),
  },
};

const variantTrackingNameMap = {
  [variants.LARGE]: 'large_level_set_banner_cta',
  [variants.SMALL]: 'small_level_set_banner_cta',
};

export const levelSetBannerStateConstants = {
  NEVER_STARTED: 'never_started', // User has not started the level set
  IN_PROGRESS: 'in_progress', // User has started the level set but not completed it
  RETAKE: 'retake', // User has completed the level set but has attempts left
  COMPLETED: 'completed', // User has completed the level set and have no attempts left
} as const;
type LevelSetBannerState = typeof levelSetBannerStateConstants[keyof typeof levelSetBannerStateConstants];

const getLevelSetBannerState = (levelSetAttempt: LevelSetAttempt): LevelSetBannerState => {
  if (levelSetAttempt.progressState === 'IN_PROGRESS') {
    return levelSetBannerStateConstants.IN_PROGRESS;
  } else if (levelSetAttempt.attemptsLeft === 0) {
    return levelSetBannerStateConstants.COMPLETED;
  } else if (levelSetAttempt.attemptsLeft > 0 && levelSetAttempt.attemptsLeft < levelSetAttempt.totalAttempts) {
    return levelSetBannerStateConstants.RETAKE;
  } else if (levelSetAttempt.attemptsLeft === levelSetAttempt.totalAttempts) {
    return levelSetBannerStateConstants.NEVER_STARTED;
  } else {
    const errorMessage = 'Invalid level set banner state, falling back to never started.';
    loggerSingleton.error(errorMessage);
    Sentry.captureException(new Error(`${errorMessage} ${JSON.stringify(levelSetAttempt)}`));
    return levelSetBannerStateConstants.NEVER_STARTED;
  }
};

export const LevelSetBanner = ({ skillId, variant, levelSetAttempt }: Props): React.ReactElement => {
  const theme = useTheme();
  const styles = variantStyleMap[variant];
  const location = useLocation();
  const { programSlug } = useParams();

  const levelSetBannerState = getLevelSetBannerState(levelSetAttempt);

  if (levelSetBannerState === levelSetBannerStateConstants.COMPLETED) {
    return (
      // We currently only support the small variant for completed level sets
      <div css={variantStyleMap[variants.SMALL].completedContainer(theme)}>
        <SuccessFilledIcon color="success" size="medium" />
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {_t('LevelSet completed')}
        </Typography2>
      </div>
    );
  }
  return (
    <div css={styles.container(theme)}>
      {variant === variants.LARGE && (
        <Typography variant="h2semibold" color="body">
          {_t('Don’t feel like these recommendations are right for you?')}
        </Typography>
      )}
      <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.supportText(theme)}>
        {levelSetBannerState === levelSetBannerStateConstants.NEVER_STARTED &&
          _t('Take a LevelSet assessment to check your current skill level')}
        {levelSetBannerState === levelSetBannerStateConstants.IN_PROGRESS &&
          _t('Complete this LevelSet assessment to check your current skill level')}
        {levelSetBannerState === levelSetBannerStateConstants.RETAKE &&
          _t('Retake this LevelSet assessment to check your current skill level (#{numberOfAttempts} attempt left)', {
            numberOfAttempts: levelSetAttempt.attemptsLeft,
          })}
      </Typography2>
      <TrackedButton
        {...Button.defaultProps}
        data-test="level-set-banner-cta"
        component="a"
        trackingName={variantTrackingNameMap[variant]}
        css={styles.ctaButton(theme)}
        variant="secondary"
        size="small"
        href={`/levelsets/${skillId}?source=${
          location.query?.source ?? 'skillPageBanner'
        }&programSlug=${programSlug}&skillId=${skillId}`}
      >
        {levelSetBannerState === levelSetBannerStateConstants.NEVER_STARTED && _t('Check your level')}
        {levelSetBannerState === levelSetBannerStateConstants.IN_PROGRESS && _t('Finish assessment')}
        {levelSetBannerState === levelSetBannerStateConstants.RETAKE && _t('Retake assessment')}
      </TrackedButton>
    </div>
  );
};

export default LevelSetBanner;
